
/**
 * Intenta copiar texto al portapapeles utilizando múltiples estrategias
 * para maximizar la compatibilidad en entornos restrictivos o asíncronos.
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  if (!text) return false;

  try { window.focus(); } catch (e) { console.warn("Error focusing window:", e); }

  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (err) {
    console.warn("[Clipboard] Falló Navigator API, intentando Legacy...", err);
  }

  try {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-9999px";
    textArea.style.top = "0";
    textArea.style.opacity = "0.01"; 
    textArea.contentEditable = "true";
    textArea.readOnly = false;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    textArea.setSelectionRange(0, 999999); 

    let successful = false;
    try {
        successful = document.execCommand('copy');
    } catch (execErr) {
        console.warn("execCommand error", execErr);
    }
    setTimeout(() => {
       document.body.removeChild(textArea);
    }, 100);
    
    if (successful) return true;
  } catch (err) {
    console.error("[Clipboard] Error fatal copiando:", err);
  }
  return false;
};

/**
 * Copia contenido HTML al portapapeles manteniendo el formato (negritas, saltos).
 * Ideal para pegar en visores médicos o editores de texto.
 */
export const copyHtmlToClipboard = async (html: string): Promise<boolean> => {
  if (!html) return false;
  
  // Si el contenido no tiene etiquetas HTML, lo convertimos a una estructura básica de párrafos
  const processedHtml = html.includes('<') ? html : html.split(/\n\n+/).map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('');

  // Envolvemos en estructura HTML estándar con estilos explícitos para forzar alineación y espaciado
  // Esto evita que programas externos (Word/PACS) apliquen justificación automática o colapsen párrafos.
  // Usamos white-space: pre-wrap para asegurar que los saltos de línea se respeten siempre.
  const fullHtml = `<!DOCTYPE html><html><head><meta charset='utf-8'><style>body{text-align:left!important;white-space:pre-wrap!important;font-family:sans-serif;line-height:1.6;color:#000000;}p,div{text-align:left!important;margin-top:0;margin-bottom:12pt;}</style></head><body style="text-align:left;white-space:pre-wrap;font-family:sans-serif;line-height:1.6;">${processedHtml}</body></html>`;

  // Texto plano para fallback
  const plainText = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<[^>]*>?/gm, '')
    .replace(/&nbsp;/g, ' ')
    .trim();

  try {
    if (navigator.clipboard && window.isSecureContext && typeof ClipboardItem !== 'undefined') {
      const blobHtml = new Blob([fullHtml], { type: 'text/html' });
      const blobText = new Blob([plainText], { type: 'text/plain' });
      const data = [new ClipboardItem({
        'text/html': blobHtml,
        'text/plain': blobText,
      })];
      await navigator.clipboard.write(data);
      return true;
    }
  } catch (err) {
    console.warn("[Clipboard] Error copiando HTML, usando fallback de texto plano", err);
  }

  // Si falla el copiado enriquecido, copiamos el texto plano
  return copyToClipboard(plainText);
};
